import { Download } from 'lucide-react';
import type React from 'react';
import { type Ref, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Frame from '@/assets/icon/Frame.png';
import { cn } from '@/lib/utils';
import Button from '../Button';
import GeneralInput, { type GeneralInputRef } from '../GeneralInput/GeneralInput';
import RadioFilter from '../RadioFilter/RadioFilter';
import type { FilteringProps } from './Filtering.types';

const Filtering = ({
  searchRef,
  customWidthSelect,
  customWidthInput,
  labelSelect,
  children,
  disableSearchInput = false,
  placeholder,
  disableSearchButton = false,
  disableFiltering = false,
  disableExportExcel = false,
  disableResetButton = false,
  handleExportExcel,
  // isSearchOnInput = false,
  showLabel = true,
  optionsRadioFilter,
  optionsRadioFilterUsage,
  isUsage,
  onSearch = () => {},
  onReset = () => {},
}: FilteringProps) => {
  const [searchInput, setSearchInput] = useState('');
  const [selectedRadio, setSelectedRadio] = useState('001002');
  const [selectedRadioUsage, setSelectedRadioUsage] = useState('002001');
  const [isRadioFilterVisible, setIsRadioFilterVisible] = useState(false);
  const { t } = useTranslation();

  const contentRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState('0px');

  const handleSearchInput = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onSearch(searchInput, selectedRadio, selectedRadioUsage);
    }
  };

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedValue = event.target.value;
    setSelectedRadio(selectedValue);
    if (selectedValue === t('cmn > all')) {
      setSelectedRadio('All');
    }
  };

  const handleRadioUsageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedValue = event.target.value;
    setSelectedRadioUsage(selectedValue);
    if (selectedValue === t('cmn > all')) {
      setSelectedRadioUsage('All');
    }
  };

  const handleReset = () => {
    setSearchInput('');
    setSelectedRadio('001002');
    setSelectedRadioUsage('All');
    onReset();
  };

  const handleSearchButtonClick = () => {
    onSearch(searchInput, selectedRadio, selectedRadioUsage);
  };

  const toggleRadioFilter = () => {
    setIsRadioFilterVisible((prevState) => !prevState);
  };

  useEffect(() => {
    if (contentRef.current) {
      setMaxHeight(isRadioFilterVisible ? `${contentRef.current.scrollHeight}px` : '0px');
    }
  }, [isRadioFilterVisible]);

  return (
    <div className="flex flex-col">
      <div className="flex gap-4 justify-between items-center flex-wrap w-full">
        <div className={cn(customWidthSelect || 'w-70', 'flex items-center gap-4')}>
          {showLabel && (
            <span className="title3medium capitalize text-au-neutral-4 py-[9px] flex-grow whitespace-nowrap">
              {labelSelect}
            </span>
          )}
          {children}
        </div>

        <div className="flex gap-[12px] ml-auto items-center flex-1 justify-end basis-[400px]">
          {!disableSearchInput && (
            <GeneralInput
              ref={searchRef as Ref<GeneralInputRef>}
              customInput={cn(customWidthInput || 'w-[400px]')}
              placeholder={placeholder}
              isSearch={true}
              value={searchInput}
              onKeyUp={handleSearchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          )}
          {!disableSearchButton && (
            <Button
              label={t('cmn > search')}
              customButton="btn-search text-[14px]! h-[36px]!"
              onClick={handleSearchButtonClick}
            />
          )}
          {!disableResetButton && (
            <Button
              label={t('cmn > reset')}
              customButton="btn-reset text-[14px]! h-[36px]!"
              onClick={handleReset}
            />
          )}
          {!disableFiltering && (
            <Button
              label={t('cmn > filtering')}
              customButton={cn(
                'flex text-[14px]! h-[36px]!',
                isRadioFilterVisible ? 'btn-filter-active' : 'btn-reset'
              )}
              isImage={Frame}
              onClick={toggleRadioFilter}
              aria-expanded={isRadioFilterVisible}
              aria-controls="filtering-panel"
            />
          )}

          {disableExportExcel && (
            <Button customButton="btn-search text-[14px]! h-[36px]!" onClick={handleExportExcel}>
              Export Excel
              <Download />
            </Button>
          )}
        </div>
      </div>

      {/* {isRadioFilterVisible && ( */}
      {/* <div className= {`${isRadioFilterVisible  ? 'block visible duration-300' : " hidden duration-300"}`}> */}

      <section
        id="filtering-panel"
        ref={contentRef}
        style={{
          maxHeight,
          transition: 'max-height 0.3s ease, opacity 0.3s ease',
          opacity: isRadioFilterVisible ? 1 : 0,
        }}
        // pt-4 lives INSIDE the collapsible panel (clipped by overflow-hidden when
        // collapsed) so the open-state spacing is preserved without leaving a
        // permanent flex gap below the toolbar when the panel is closed.
        className="overflow-hidden pt-4"
      >
        {isUsage ? (
          <>
            <RadioFilter
              name="Usage"
              label={t('002')}
              options={optionsRadioFilterUsage ?? []}
              value={selectedRadioUsage}
              onChange={handleRadioUsageChange}
            />
            <RadioFilter
              name="Deleted"
              label={t('001')}
              options={optionsRadioFilter ?? []}
              value={selectedRadio}
              onChange={handleRadioChange}
            />
          </>
        ) : (
          <RadioFilter
            name="Deleted"
            label={t('001')}
            options={optionsRadioFilter ?? []}
            value={selectedRadio}
            onChange={handleRadioChange}
          />
        )}
      </section>
      {/* )} */}
    </div>
  );
};

export default Filtering;
